import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import { omittedTagsPlaceholder } from "@av/apps/recruitment/ats-common/components/omitted-tags-placeholder";
import { resolveLocationTypeTranslation } from "@av/apps/recruitment/ats-common/services/job-posts/job-posts";
import { type JobPostFilters, locationTypeValues } from "@av/apps/recruitment/ats-common/types";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Select } from "@av/components/atoms/Select";

import type { DefaultOptionType } from "antd/es/select";

const useStyles = createStyles(({ token, css }) => ({
  filtersCard: css`
    gap: ${token.marginSM}px;
    margin-bottom: ${token.marginLG}px;
  `,
  searchInput: css`
    flex: 1 1 320px;
    min-width: 220px;
  `,
  filtersRow: css`
    gap: ${token.marginSM}px;
  `,
  selectControl: css`
    min-width: 160px;
  `,
  formItem: css`
    & .ant-form-item-control-input {
      width: max-content;
    }
  `,
}));

interface FiltersBarProps {
  filters: JobPostFilters;
  onFiltersChange: (newFilter: JobPostFilters) => void;
  locationOptions: DefaultOptionType[];
  testIdPrefix: string;
}

export function FiltersBar({
  filters,
  onFiltersChange,
  locationOptions,
  testIdPrefix,
}: FiltersBarProps) {
  const { styles } = useStyles();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const locationTypeOptions = locationTypeValues.map((locationType) => ({
    value: locationType,
    label: resolveLocationTypeTranslation(t, locationType),
  }));

  /**
   * Whenever filters in the url change apply those to the form
   */
  useEffect(() => {
    form.setFieldsValue(filters);
  }, [filters, form]);

  return (
    <Form
      form={form}
      style={{ maxWidth: "100%", marginBottom: 0 }}
      onValuesChange={(_, allValues) => onFiltersChange(allValues)}
    >
      <Flex wrap className={styles.filtersCard}>
        <Form.Item name="searchText" testIdPrefix={`${testIdPrefix}-search-box`}>
          <Input.Search
            style={{ width: 460 }}
            size="large"
            allowClear
            testIdPrefix={testIdPrefix}
            className={styles.searchInput}
            placeholder={t("recruitment.jobs.inputPlaceholders_publicJobsSearch")}
            prefix={<SearchOutlined />}
          />
        </Form.Item>

        <Flex wrap className={styles.filtersRow}>
          <Form.Item
            name="subdivisionCodes"
            testIdPrefix={`${testIdPrefix}-locations`}
            className={styles.formItem}
          >
            <Select
              allowClear
              size="large"
              testIdPrefix={`${testIdPrefix}-location-filter`}
              className={styles.selectControl}
              mode="multiple"
              placeholder={t("recruitment.jobs.inputPlaceholders_locations")}
              optionLabelProp="label"
              optionFilterProp="label"
              maxTagPlaceholder={omittedTagsPlaceholder}
              options={locationOptions}
            />
          </Form.Item>

          <Form.Item name="locationType" testIdPrefix={`${testIdPrefix}-types`}>
            <Select
              allowClear
              size="large"
              testIdPrefix={`${testIdPrefix}-type-filter`}
              className={styles.selectControl}
              placeholder={t("recruitment.jobs.inputPlaceholders_type")}
              options={locationTypeOptions}
            />
          </Form.Item>
        </Flex>
      </Flex>
    </Form>
  );
}
